import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Wallet, TrendingUp, TrendingDown, Plus, Search, Trash2,
  Download, FileText, Edit3, X, RefreshCw,
} from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import api, { getActiveStudioId } from "@/lib/api";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { ReceiptViewerModal } from "@/components/receipts/ReceiptViewerModal";

const METHOD_LABELS = {
  cash: "Cash",
  upi: "UPI",
  bank_transfer: "Bank Transfer",
  card: "Card",
  other: "Other",
};

// Feb 2026 — human-readable labels for the enum values shipped by the backend
// (`GET /api/ledger/categories`). Keep in sync with EXPENSE_CATEGORIES /
// REVENUE_CATEGORIES in backend/models.py.
const CATEGORY_LABELS = {
  // Revenue
  sangeet: "Sangeet",
  corporate_shows: "Corporate Shows",
  other_revenue: "Other Revenue",
  // Expense
  rent: "Rent",
  electricity: "Electricity",
  instructor_payout: "Instructor Payout",
  maintenance: "Maintenance",
  other_expenses: "Other Expenses",
};
const catLabel = (c) => CATEGORY_LABELS[c] || (c ? c.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "—");

const initialForm = () => ({
  entry_type: "revenue",
  category: "sangeet",
  party_name: "",
  party_phone: "",
  amount: "",
  payment_method: "cash",
  entry_date: format(new Date(), "yyyy-MM-dd"),
  notes: "",
  instructor_id: "",
});

const LedgerManagement = ({ user, onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ revenue: 0, expense: 0, net: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const now = new Date();
  const [typeFilter, setTypeFilter] = useState("all"); // all | revenue | expense
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(now), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(endOfMonth(now), "yyyy-MM-dd"));
  const [search, setSearch] = useState("");

  // Categories
  const [categories, setCategories] = useState({ expense: [], revenue: [] });
  // Feb 2026 — instructor list for the Instructor Payout tag
  const [instructors, setInstructors] = useState([]);

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [saving, setSaving] = useState(false);

  // Receipt viewer
  const [receipt, setReceipt] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);

  useEffect(() => {
    api.get("/ledger/categories").then((r) => setCategories(r.data || {})).catch(() => {});
    // Instructors used only when category = Instructor Payout (expense)
    api.get("/instructors")
      .then((r) => setInstructors(r.data?.instructors || []))
      .catch(() => setInstructors([]));
  }, []);

  const fetchEntries = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("entry_type", typeFilter);
      if (categoryFilter) params.append("category", categoryFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (search.trim()) params.append("q", search.trim());
      const res = await api.get(`/ledger/entries?${params.toString()}`);
      setEntries(res.data.entries || []);
      setTotals(res.data.totals || { revenue: 0, expense: 0, net: 0, count: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load ledger");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [typeFilter, categoryFilter, dateFrom, dateTo, search]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const openAdd = () => {
    setEditingId(null);
    setForm(initialForm());
    setDialogOpen(true);
  };

  const openEdit = (entry) => {
    setEditingId(entry.entry_id);
    setForm({
      entry_type: entry.entry_type,
      category: entry.category,
      party_name: entry.party_name || "",
      party_phone: entry.party_phone || "",
      amount: String(entry.amount ?? ""),
      payment_method: entry.payment_method || "cash",
      entry_date: entry.entry_date || "",
      notes: entry.notes || "",
      instructor_id: entry.instructor_id || "",
    });
    setDialogOpen(true);
  };

  const submitForm = async () => {
    if (!form.party_name.trim()) { toast.error("Party name is required"); return; }
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be greater than 0"); return; }
    // Feb 2026 — Instructor Payout requires an instructor tag
    if (form.entry_type === "expense" && form.category === "instructor_payout" && !form.instructor_id) {
      toast.error("Please tag the instructor being paid");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        entry_type: form.entry_type,
        category: form.category,
        party_name: form.party_name.trim(),
        party_phone: form.party_phone.trim() || null,
        amount: amt,
        payment_method: form.payment_method,
        entry_date: form.entry_date,
        notes: form.notes.trim() || null,
      };
      if (form.entry_type === "expense" && form.category === "instructor_payout") {
        payload.instructor_id = form.instructor_id;
      } else {
        // Clear tag when switching away from instructor_payout on edit
        payload.instructor_id = null;
      }
      if (editingId) {
        await api.put(`/ledger/entries/${editingId}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post("/ledger/entries", payload);
        toast.success(`${form.entry_type === "revenue" ? "Revenue" : "Expense"} added`);
      }
      setDialogOpen(false);
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Delete this ${entry.entry_type} entry (${entry.party_name})? Soft-delete only — record is preserved for audit.`)) return;
    try {
      await api.delete(`/ledger/entries/${entry.entry_id}`);
      toast.success("Entry deleted");
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const issueReceipt = async (entry) => {
    try {
      const res = await api.post(`/ledger/entries/${entry.entry_id}/receipt`);
      if (res.data?.receipt) {
        setReceipt(res.data.receipt);
        setReceiptOpen(true);
        if (res.data.reissued) toast.success("Receipt generated");
      }
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate receipt");
    }
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("entry_type", typeFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const studioId = getActiveStudioId();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/ledger/export?${params.toString()}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          ...(studioId ? { "X-Studio-Id": studioId } : {}),
        },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ledger_${dateFrom}_${dateTo}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Failed to export Excel");
    }
  };

  const availableCategories = form.entry_type === "revenue" ? categories.revenue : categories.expense;

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />

      <div className="p-4 md:p-8 pb-20 md:pb-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900" data-testid="ledger-title">
              Expense & Revenue Ledger
            </h1>
            <p className="text-sm text-slate-600 mt-1">Track non-student income and studio expenses.</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={exportExcel}
              variant="outline"
              className="rounded-full"
              data-testid="ledger-export-btn"
            >
              <Download className="w-4 h-4 mr-1.5" /> Excel
            </Button>
            <Button
              onClick={openAdd}
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="ledger-add-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add Entry
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-emerald-100"
            data-testid="ledger-summary-revenue"
          >
            <div className="flex items-center gap-2 text-emerald-700 mb-2">
              <TrendingUp className="w-5 h-5" />
              <span className="text-xs font-semibold uppercase tracking-wider">Revenue</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">₹{totals.revenue.toLocaleString("en-IN")}</p>
            <p className="text-[11px] text-slate-500 mt-1">{totals.count} entries in view</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-rose-100"
            data-testid="ledger-summary-expense"
          >
            <div className="flex items-center gap-2 text-rose-700 mb-2">
              <TrendingDown className="w-5 h-5" />
              <span className="text-xs font-semibold uppercase tracking-wider">Expense</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">₹{totals.expense.toLocaleString("en-IN")}</p>
            <p className="text-[11px] text-slate-500 mt-1">In selected period</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`bg-white rounded-2xl p-4 sm:p-5 shadow-sm border ${
              totals.net >= 0 ? "border-blue-100" : "border-amber-100"
            }`}
            data-testid="ledger-summary-net"
          >
            <div className={`flex items-center gap-2 mb-2 ${totals.net >= 0 ? "text-blue-700" : "text-amber-700"}`}>
              <Wallet className="w-5 h-5" />
              <span className="text-xs font-semibold uppercase tracking-wider">Net</span>
            </div>
            <p className={`text-2xl font-bold ${totals.net >= 0 ? "text-slate-900" : "text-amber-800"}`}>
              ₹{totals.net.toLocaleString("en-IN")}
            </p>
            <p className="text-[11px] text-slate-500 mt-1">Revenue − Expense</p>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="col-span-2 md:col-span-1">
              <Label className="text-xs">Type</Label>
              <select
                data-testid="ledger-filter-type"
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setCategoryFilter(""); }}
                className="w-full mt-1 h-10 rounded-xl border border-slate-200 px-3 bg-white text-sm"
              >
                <option value="all">All</option>
                <option value="revenue">Revenue</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div className="col-span-2 md:col-span-1">
              <Label className="text-xs">Category</Label>
              <select
                data-testid="ledger-filter-category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full mt-1 h-10 rounded-xl border border-slate-200 px-3 bg-white text-sm"
                disabled={typeFilter === "all"}
              >
                <option value="">All</option>
                {(typeFilter === "revenue" ? categories.revenue : typeFilter === "expense" ? categories.expense : []).map((c) => (
                  <option key={c} value={c}>{catLabel(c)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" data-testid="ledger-filter-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 h-10 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" data-testid="ledger-filter-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 h-10 rounded-xl" />
            </div>
            <div className="col-span-2 md:col-span-2">
              <Label className="text-xs">Search (party name)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="ledger-filter-search"
                  placeholder="Vendor, customer, event..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mt-1 h-10 rounded-xl pl-9"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Entries list */}
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl h-16 animate-pulse border border-slate-100" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 border border-slate-100 text-center text-slate-500" data-testid="ledger-empty">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No entries match these filters.</p>
            <Button onClick={openAdd} variant="outline" className="mt-4 rounded-full">
              <Plus className="w-4 h-4 mr-1.5" /> Add your first entry
            </Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="ledger-list">
            {entries.map((e) => (
              <motion.div
                key={e.entry_id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-4 border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all"
                data-testid={`ledger-entry-${e.entry_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                          e.entry_type === "revenue"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-rose-50 text-rose-700"
                        }`}
                      >
                        {e.entry_type}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">
                        {catLabel(e.category)}
                      </span>
                      <span className="text-[11px] text-slate-500">{e.entry_date}</span>
                      {e.receipt_id && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700">
                          Receipt issued
                        </span>
                      )}
                      {e.instructor_id && e.instructor_name && (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-50 text-violet-700"
                          data-testid={`ledger-entry-instructor-${e.entry_id}`}
                        >
                          → {e.instructor_name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-slate-900 truncate">{e.party_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {METHOD_LABELS[e.payment_method] || e.payment_method}
                      {e.party_phone && ` · ${e.party_phone}`}
                      {e.notes && ` · ${e.notes}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <p
                      className={`text-lg font-bold ${
                        e.entry_type === "revenue" ? "text-emerald-700" : "text-rose-700"
                      }`}
                    >
                      {e.entry_type === "revenue" ? "+" : "−"} ₹{Number(e.amount).toLocaleString("en-IN")}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => issueReceipt(e)}
                      title="Generate / view receipt"
                      data-testid={`ledger-receipt-${e.entry_id}`}
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => openEdit(e)}
                      title="Edit"
                      data-testid={`ledger-edit-${e.entry_id}`}
                    >
                      <Edit3 className="w-4 h-4 text-slate-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => deleteEntry(e)}
                      title="Delete"
                      data-testid={`ledger-delete-${e.entry_id}`}
                    >
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {form.entry_type === "revenue" ? (
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              ) : (
                <TrendingDown className="w-5 h-5 text-rose-600" />
              )}
              {editingId ? "Edit Entry" : `Add ${form.entry_type === "revenue" ? "Revenue" : "Expense"}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {!editingId && (
              <div className="grid grid-cols-2 gap-2">
                {["revenue", "expense"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`ledger-type-${t}`}
                    onClick={() => {
                      const defaultCat = t === "revenue" ? categories.revenue?.[0] : categories.expense?.[0];
                      // Reset instructor tag when switching between revenue/expense
                      setForm({ ...form, entry_type: t, category: defaultCat || "", instructor_id: "" });
                    }}
                    className={`h-11 rounded-xl border text-sm font-semibold transition-colors ${
                      form.entry_type === t
                        ? t === "revenue"
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-rose-600 text-white border-rose-600"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {t === "revenue" ? "Revenue" : "Expense"}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category *</Label>
                <select
                  data-testid="ledger-form-category"
                  value={form.category}
                  onChange={(e) => setForm({
                    ...form,
                    category: e.target.value,
                    // Clear instructor tag when moving away from Instructor Payout
                    instructor_id: e.target.value === "instructor_payout" ? form.instructor_id : "",
                  })}
                  className="w-full mt-1 h-11 rounded-xl border border-slate-200 px-3 bg-white text-sm"
                >
                  {availableCategories.map((c) => (
                    <option key={c} value={c}>{catLabel(c)}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Date *</Label>
                <Input
                  data-testid="ledger-form-date"
                  type="date"
                  value={form.entry_date}
                  onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                  className="mt-1 h-11 rounded-xl"
                />
              </div>
            </div>

            {/* Feb 2026 — Instructor tag for the Instructor Payout expense category */}
            {form.entry_type === "expense" && form.category === "instructor_payout" && (
              <div>
                <Label className="text-xs">Instructor *</Label>
                <select
                  data-testid="ledger-form-instructor"
                  value={form.instructor_id}
                  onChange={(e) => setForm({ ...form, instructor_id: e.target.value })}
                  className="w-full mt-1 h-11 rounded-xl border border-violet-200 bg-violet-50/40 px-3 text-sm"
                >
                  <option value="">— Select instructor —</option>
                  {instructors.filter((i) => i.is_active !== false).map((i) => (
                    <option key={i.instructor_id || i.user_id} value={i.instructor_id || i.user_id}>
                      {i.name}
                      {i.role === "admin" ? "  •  Admin" : ""}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  Links this payout to the instructor&apos;s record for cross-reference with Payroll.
                </p>
              </div>
            )}

            <div>
              <Label className="text-xs">
                {form.entry_type === "revenue" ? "Payer" : "Payee"} Name *
              </Label>
              <Input
                data-testid="ledger-form-party"
                value={form.party_name}
                onChange={(e) => setForm({ ...form, party_name: e.target.value })}
                placeholder={form.entry_type === "revenue" ? "e.g. Workshop batch, T-shirt sales" : "e.g. Landlord, Electric board"}
                className="mt-1 h-11 rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount (₹) *</Label>
                <Input
                  data-testid="ledger-form-amount"
                  type="number"
                  min="0"
                  step="1"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="mt-1 h-11 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-xs">Method *</Label>
                <select
                  data-testid="ledger-form-method"
                  value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                  className="w-full mt-1 h-11 rounded-xl border border-slate-200 px-3 bg-white text-sm"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Phone (optional)</Label>
              <Input
                data-testid="ledger-form-phone"
                value={form.party_phone}
                onChange={(e) => setForm({ ...form, party_phone: e.target.value })}
                placeholder="10-digit number"
                className="mt-1 h-11 rounded-xl"
              />
            </div>

            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input
                data-testid="ledger-form-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Reference no., description..."
                className="mt-1 h-11 rounded-xl"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 rounded-full"
              >
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
              <Button
                data-testid="ledger-form-submit"
                onClick={submitForm}
                disabled={saving}
                className={`flex-1 rounded-full ${
                  form.entry_type === "revenue" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
                }`}
              >
                {saving ? "Saving..." : editingId ? "Update" : "Save Entry"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav user={user} onLogout={onLogout} />

      <ReceiptViewerModal
        open={receiptOpen}
        onOpenChange={(v) => { setReceiptOpen(v); if (!v) setReceipt(null); }}
        receipt={receipt}
      />
    </div>
  );
};

export default LedgerManagement;
